/**
 * reset-password.tsx — Form untuk apply password baru dari token email.
 * Lives at `/reset-password?token=<raw>`. Tidak butuh session.
 */
import { useState, type ReactNode, type FormEvent } from 'react';
import { createFileRoute, useSearch, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { resetPasswordMutation } from '../lib/api/mutations.js';
import { PageHeader } from '../components/ui/PageHeader.js';

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search['token'] === 'string' ? search['token'] : undefined,
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage(): ReactNode {
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const resetMut = useMutation(resetPasswordMutation());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password baru minimal 8 karakter.');
      return;
    }
    if (password !== confirm) {
      setError('Password tidak cocok.');
      return;
    }
    try {
      await resetMut.mutateAsync({ token, newPassword: password });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal reset password.');
    }
  }

  if (!token) {
    return (
      <main className="page-shell" data-testid="reset-password-page">
        <PageHeader
          eyebrow="Reset password"
          title="Token tidak ditemukan"
          description="Buka link reset dari email Anda secara lengkap."
        />
        <p>
          <Link to="/login" className="button button-primary" search={{ error: undefined, redirect: undefined }}>
            Kembali ke halaman masuk
          </Link>
        </p>
      </main>
    );
  }

  if (resetMut.isSuccess) {
    return (
      <main className="page-shell" data-testid="reset-password-page">
        <PageHeader
          eyebrow="Reset password"
          title="Berhasil!"
          description="Password kamu sudah direset. Silakan masuk dengan password baru."
        />
        <p>
          <Link to="/login" className="button button-primary" search={{ error: undefined, redirect: undefined }}>
            Masuk
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="page-shell" data-testid="reset-password-page">
      <PageHeader
        eyebrow="Reset password"
        title="Password baru"
        description="Masukkan password baru untuk akun Anda."
      />
      <form className="stack" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="reset-password-new">Password baru</label>
          <input
            id="reset-password-new"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            disabled={resetMut.isPending}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-new"
          />
        </div>
        <div className="field">
          <label htmlFor="reset-password-confirm">Konfirmasi password</label>
          <input
            id="reset-password-confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            disabled={resetMut.isPending}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-password-confirm"
          />
        </div>
        <button
          type="submit"
          className="button button-primary"
          disabled={resetMut.isPending}
          data-testid="reset-password-submit"
        >
          {resetMut.isPending ? 'Menyimpan…' : 'Reset password'}
        </button>
        {error ? (
          <p className="form-message is-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </main>
  );
}
